import toast from 'react-hot-toast';
import Request from '@/services/util/request-service';
import { ActivityData, SubunitData, UnitData } from 'course-types';

/**
 * Service for handling course content reordering operations.
 * Centralizes API calls and error handling for unit, subunit, and activity reordering.
 */
const CourseReorderService = {
  /**
   * Reorder units within a course
   */
  async reorderUnits(
    unitRecord: { [unitId: string]: number },
    courseId: string,
  ): Promise<{ success: boolean; error?: string; courseData?: any }> {
    try {
      const requestResponse = await Request.post('course/reorder/units', {
        unitRecord,
        courseId,
      });

      const response = await requestResponse?.json();

      if (!response || response.status !== 'success') {
        const errorMessage = response?.data || 'Unauthorized';
        console.error('Unit reorder API Error:', errorMessage);
        toast.error(`Error reordering units: ${errorMessage}`);
        return { success: false, error: errorMessage };
      }

      toast.success('Units reordered successfully');

      const courseData = {
        id: response.data.courseId,
        name: response.data.courseStructure.name,
        units: response.data.courseStructure.units,
      };

      return { success: true, courseData: courseData };
    } catch (error) {
      console.error('Unit reorder request failed:', error);
      toast.error('Failed to reorder units. Please try again.');
      return { success: false, error: String(error) };
    }
  },

  /**
   * Reorder subunits and unit-level activities within a unit
   */
  async reorderUnitContent(
    subunitRecord: { [subunitId: string]: number },
    activityRecord: {
      quiz: Record<string, number>;
      document: Record<string, number>;
      video: Record<string, number>;
      resource: Record<string, number>;
      assignment: Record<string, number>;
    },
    unitId: string,
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const requestResponse = await Request.post('course/reorder/subunits', {
        subunitRecord,
        activityRecord,
        unitId,
      });

      const response = await requestResponse?.json();

      if (!response || response.status !== 'success') {
        const errorMessage = response?.data || 'Unauthorized';
        console.error('Unit content reorder API Error:', errorMessage);
        toast.error(`Error reordering unit content: ${errorMessage}`);
        return { success: false, error: errorMessage };
      }

      toast.success('Unit content reordered successfully');
      return { success: true };
    } catch (error) {
      console.error('Unit content reorder request failed:', error);
      toast.error('Failed to reorder unit content. Please try again.');
      return { success: false, error: String(error) };
    }
  },

  /**
   * Reorder activities within a subunit
   */
  async reorderSubunitActivities(
    activityRecord: {
      quiz: Record<string, number>;
      document: Record<string, number>;
      video: Record<string, number>;
      resource: Record<string, number>;
    },
    subunitId: string,
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const requestResponse = await Request.post('content/reorder', {
        activityRecord,
        subunitId,
      });

      const response = await requestResponse?.json();

      if (!response || response.status !== 'success') {
        const errorMessage = response?.data || 'Unauthorized';
        console.error('Subunit activity reorder API Error:', errorMessage);
        toast.error(`Error reordering subunit activities: ${errorMessage}`);
        return { success: false, error: errorMessage };
      }

      toast.success('Subunit activities reordered successfully');
      return { success: true };
    } catch (error) {
      console.error('Subunit activity reorder request failed:', error);
      toast.error('Failed to reorder subunit activities. Please try again.');
      return { success: false, error: String(error) };
    }
  },

  /**
   * Build activity record from sorted activities array
   */
  buildActivityRecord(activities: ActivityData[]): {
    quiz: Record<string, number>;
    document: Record<string, number>;
    video: Record<string, number>;
    resource: Record<string, number>;
    assignment: Record<string, number>;
  } {
    const activityRecord: {
      quiz: Record<string, number>;
      document: Record<string, number>;
      video: Record<string, number>;
      resource: Record<string, number>;
      assignment: Record<string, number>;
    } = { quiz: {}, document: {}, video: {}, resource: {}, assignment: {} };

    activities.forEach((activity, index) => {
      activityRecord[activity.type][activity.id] = index;
    });

    return activityRecord;
  },

  /**
   * Build combined activity and subunit records from sorted content
   */
  buildUnitContentRecords(sortedContents: Array<(SubunitData | ActivityData) & { id: string }>): {
    subunitRecord: { [subunitId: string]: number };
    activityRecord: {
      quiz: Record<string, number>;
      document: Record<string, number>;
      video: Record<string, number>;
      resource: Record<string, number>;
      assignment: Record<string, number>;
    };
  } {
    const subunitRecord: { [subunitId: string]: number } = {};
    const activityRecord: {
      quiz: Record<string, number>;
      document: Record<string, number>;
      video: Record<string, number>;
      resource: Record<string, number>;
      assignment: Record<string, number>;
    } = { quiz: {}, document: {}, video: {}, resource: {}, assignment: {} };

    sortedContents.forEach((item, index) => {
      const isActivity = ['document', 'video', 'quiz', 'resource', 'assignment'].includes(
        (item as any)?.type,
      );
      if (isActivity) {
        const type = (item as any).type as
          | 'document'
          | 'video'
          | 'quiz'
          | 'resource'
          | 'assignment';
        activityRecord[type][(item as any).id] = index;
      } else {
        subunitRecord[(item as any).id] = index;
      }
    });

    return { subunitRecord, activityRecord };
  },
};

export default CourseReorderService;
